import { useCallback, useEffect, useState } from 'react';
import { getHandlingTimeNormen, zetHandlingTimeNormen, type HandlingTimeNormen } from '../handlingTime.api';

/**
 * Story 31.5 — de twee normen ophalen en zetten.
 *
 * > [!note] Met de hand, en niet via react-query
 * > De rest van dit scherm haalt zijn cijfers via `useQuery` op. Deze twee hooks doen het met kale
 * > React-toestand, en dat is een bewuste keuze: het behandeltijdscherm wordt in zijn eigen toetsen
 * > kaal weergegeven, zonder `QueryClientProvider` eromheen — een `useQuery` erbij laat elk van die
 * > toetsen omvallen op "No QueryClient set", ook de toetsen die met de normen niets te maken
 * > hebben.
 * >
 * > Wat we daarmee opgeven is de gedeelde kast van react-query. Dat weegt hier licht: de normen
 * > zijn twee getallen, ze worden per schermweergave één keer opgehaald, en er is precies één
 * > lezer.
 */

export interface NormenLezing {
  data: HandlingTimeNormen | undefined;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

function alsFout(oorzaak: unknown): Error {
  return oorzaak instanceof Error ? oorzaak : new Error(String(oorzaak));
}

/**
 * De bewaarde normen.
 *
 * Één keer per schermweergave. Geen herpoging: staat de poort dicht of mag deze bezoeker er niet
 * bij, dan wordt dat niet beter door de vraag te herhalen — precies zoals de cijfers ernaast.
 */
export function useHandlingTimeNormen(): NormenLezing {
  const [data, setData] = useState<HandlingTimeNormen | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    // Een antwoord dat binnenkomt nadat het scherm weg is mag niets meer zetten: dat zou een
    // toestandwijziging op een verdwenen onderdeel zijn.
    let levend = true;
    getHandlingTimeNormen()
      .then((normen) => { if (levend) { setData(normen); setError(null); } })
      .catch((oorzaak: unknown) => { if (levend) setError(alsFout(oorzaak)); })
      .finally(() => { if (levend) setIsLoading(false); });
    return () => { levend = false; };
  }, []);

  return { data, isLoading, isError: error !== null, error };
}

export interface NormenSchrijving {
  mutate: (normen: HandlingTimeNormen) => void;
  /** De laatst BEWAARDE normen, zodat het scherm ze meteen gebruikt zonder opnieuw op te halen. */
  data: HandlingTimeNormen | undefined;
  isPending: boolean;
  isError: boolean;
  error: Error | null;
}

/**
 * De normen zetten (AC-2).
 *
 * Het antwoord draagt de goedgekeurde normen, en die worden hier bewaard: zo werkt een wijziging
 * meteen door op alle drie de plekken, zonder een tweede vraag.
 *
 * Een MISLUKKING blijft hier staan en gaat niet naar een melding in de hoek: een geweigerde norm
 * vraagt om een andere invoer, en dat hoort te lezen te zijn bij het veld waar die invoer staat.
 */
export function useZetHandlingTimeNormen(): NormenSchrijving {
  const [data, setData] = useState<HandlingTimeNormen | undefined>(undefined);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mutate = useCallback((normen: HandlingTimeNormen) => {
    setIsPending(true);
    setError(null);
    void zetHandlingTimeNormen(normen)
      .then((bewaard) => { setData(bewaard); })
      .catch((oorzaak: unknown) => { setError(alsFout(oorzaak)); })
      .finally(() => { setIsPending(false); });
  }, []);

  return { mutate, data, isPending, isError: error !== null, error };
}
